import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { DialogContent, Divider, FormControl, FormControlLabel, Paper, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'

import { toast } from 'react-toastify'

import T from 'T'
import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import { useLazyGetAccountRentalFeeSettingsQuery, useUpdateAccountRentalFeeSettingsMutation } from 'api/accounts/accountBillingSettings'
import { useLazyGetRentalBillingSettingsVersionsQuery } from 'api/entity-version/entityVersionCrud'
import useVersionsState from './common/useVersionsState'
import SettingsHistoryAccordion from './common/SettingsHistoryAccordion'
import InheritBillingProfileCheckbox from './common/InheritBillingProfileCheckbox'

const FORM_DEFAULTS = {
  incompleteRentalPeriodsEnabled: 'true',
  incompleteRentalPeriodsInherit: true,
  rentalFeeOnSuspensionEnabled: 'true',
  rentalFeeOnSuspensionInherit: true,
}

const RentalFeeSettingsDialog = ({ open = false, onClose = noop, accountId }) => {
  const [getRentalFeeSettings, { data, isFetching }] = useLazyGetAccountRentalFeeSettingsQuery()
  const [updateRentalFeeSettings, { isLoading: isUpdating }] = useUpdateAccountRentalFeeSettingsMutation()
  const [getVersions, { data: versionsData, isLoading: isVersionsLoading, isFetching: isVersionsFetching, isSuccess: isVersionsSuccess }] =
    useLazyGetRentalBillingSettingsVersionsQuery()

  const {
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({ defaultValues: FORM_DEFAULTS })

  const incompleteRentalPeriodsEnabled = watch('incompleteRentalPeriodsEnabled')
  const incompleteRentalPeriodsInherit = watch('incompleteRentalPeriodsInherit')
  const rentalFeeOnSuspensionEnabled = watch('rentalFeeOnSuspensionEnabled')
  const rentalFeeOnSuspensionInherit = watch('rentalFeeOnSuspensionInherit')
  const billingProfile = get(data, 'billingProfile', null)
  const billingProfileName = get(billingProfile, 'name', '')
  const { versions, hasVersions, versionsLoading } = useVersionsState({
    versionsData,
    isVersionsLoading,
    isFetching: isVersionsFetching,
    isVersionsSuccess,
  })

  useEffect(() => {
    if (open && accountId) {
      getRentalFeeSettings({ accountId }).unwrap().catch(handleError)
      getVersions({ accountId }).unwrap().catch(handleError)
    }
  }, [open, accountId, getRentalFeeSettings, getVersions])

  useEffect(() => {
    if (data) {
      reset({
        incompleteRentalPeriodsEnabled: String(get(data, 'incompleteRentalPeriods.enabled', true)),
        incompleteRentalPeriodsInherit: get(data, 'incompleteRentalPeriods.inheritFromBillingProfile', true),
        rentalFeeOnSuspensionEnabled: String(get(data, 'rentalFeeOnSuspension.enabled', true)),
        rentalFeeOnSuspensionInherit: get(data, 'rentalFeeOnSuspension.inheritFromBillingProfile', true),
      })
    }
  }, [data, reset])

  const handleIncompleteRentalPeriodsChange = useCallback(
    event => {
      setValue('incompleteRentalPeriodsEnabled', event.target.value, { shouldDirty: true })
    },
    [setValue]
  )

  const handleIncompleteRentalPeriodsInheritChange = useCallback(
    event => {
      setValue('incompleteRentalPeriodsInherit', event.target.checked, { shouldDirty: true })
    },
    [setValue]
  )

  const handleRentalFeeOnSuspensionChange = useCallback(
    event => {
      setValue('rentalFeeOnSuspensionEnabled', event.target.value, { shouldDirty: true })
    },
    [setValue]
  )

  const handleRentalFeeOnSuspensionInheritChange = useCallback(
    event => {
      setValue('rentalFeeOnSuspensionInherit', event.target.checked, { shouldDirty: true })
    },
    [setValue]
  )

  const getVersionChangeLines = useCallback(version => {
    const lines = []
    if ('incompleteRentalPeriods' in version) {
      const enabled = get(version, 'incompleteRentalPeriods.enabled')
      if (enabled !== undefined) {
        lines.push(`${T.INCOMPLETE_RENTAL_PERIODS}: ${enabled ? T.ON : T.OFF}`)
      }
      const inherit = get(version, 'incompleteRentalPeriods.inheritFromBillingProfile')
      if (inherit !== undefined) {
        lines.push(`${T.INCOMPLETE_RENTAL_PERIODS} inherit from profile: ${inherit ? T.ON : T.OFF}`)
      }
    }
    if ('rentalFeeOnSuspension' in version) {
      const enabled = get(version, 'rentalFeeOnSuspension.enabled')
      if (enabled !== undefined) {
        lines.push(`${T.SERVICE_SUSPENSION}: ${enabled ? T.ON : T.OFF}`)
      }
      const inherit = get(version, 'rentalFeeOnSuspension.inheritFromBillingProfile')
      if (inherit !== undefined) {
        lines.push(`${T.SERVICE_SUSPENSION} inherit from profile: ${inherit ? T.ON : T.OFF}`)
      }
    }
    if (lines.length === 0) {
      lines.push('Settings updated')
    }
    return lines
  }, [])

  const handleUpdate = useCallback(() => {
    const values = getValues()
    updateRentalFeeSettings({
      accountId,
      incompleteRentalPeriods: {
        enabled: values.incompleteRentalPeriodsEnabled === 'true',
        inheritFromBillingProfile: values.incompleteRentalPeriodsInherit,
      },
      rentalFeeOnSuspension: {
        enabled: values.rentalFeeOnSuspensionEnabled === 'true',
        inheritFromBillingProfile: values.rentalFeeOnSuspensionInherit,
      },
    })
      .unwrap()
      .then(() => {
        toast.success(T.SETTINGS_SAVED_SUCCESSFULLY)
        onClose()
      })
      .catch(handleError)
  }, [accountId, getValues, updateRentalFeeSettings, onClose])

  return (
    <HHBaseDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <HHDialogTitle title={T.RENTAL_FEE_SETTINGS} onClose={onClose} />
      <DialogContent>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {T.INCOMPLETE_RENTAL_PERIODS}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {T.INVOICE_INCOMPLETE_RENTAL_PERIODS_DESCRIPTION}
          </Typography>
          <FormControl component="fieldset" sx={{ mb: 1, width: '100%' }}>
            <RadioGroup
              row
              value={incompleteRentalPeriodsEnabled}
              onChange={handleIncompleteRentalPeriodsChange}
              sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
            >
              <FormControlLabel
                value="true"
                disabled={incompleteRentalPeriodsInherit}
                control={<Radio size="small" />}
                label={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
                      {T.ON}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textTransform="lowercase">
                      ({T.DEFAULT})
                    </Typography>
                  </Stack>
                }
                sx={{ width: '50%', mx: 0 }}
              />
              <FormControlLabel
                value="false"
                disabled={incompleteRentalPeriodsInherit}
                control={<Radio size="small" />}
                label={
                  <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
                    {T.OFF}
                  </Typography>
                }
                sx={{ width: '50%', mx: 0 }}
              />
            </RadioGroup>
          </FormControl>
          <Divider sx={{ my: 1 }} />
          <InheritBillingProfileCheckbox
            checked={incompleteRentalPeriodsInherit}
            onChange={handleIncompleteRentalPeriodsInheritChange}
            billingProfileName={billingProfileName}
          >
            {billingProfile && (
              <Typography variant="body2" color="text.secondary">
                {T.INCOMPLETE_RENTAL_PERIODS}
                {': '}
                <strong>{billingProfile.incompleteRentalPeriodsEnabled ? T.ON : T.OFF}</strong>
              </Typography>
            )}
          </InheritBillingProfileCheckbox>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 6 }}>
            {T.SERVICE_SUSPENSION}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {T.INVOICE_RENTAL_FEES_WHEN_SUSPENDED}
          </Typography>
          <FormControl component="fieldset" sx={{ mb: 1, width: '100%' }}>
            <RadioGroup
              row
              value={rentalFeeOnSuspensionEnabled}
              onChange={handleRentalFeeOnSuspensionChange}
              sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
            >
              <FormControlLabel
                value="true"
                disabled={rentalFeeOnSuspensionInherit}
                control={<Radio size="small" />}
                label={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
                      {T.ON}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textTransform="lowercase">
                      ({T.DEFAULT})
                    </Typography>
                  </Stack>
                }
                sx={{ width: '50%', mx: 0 }}
              />
              <FormControlLabel
                value="false"
                disabled={rentalFeeOnSuspensionInherit}
                control={<Radio size="small" />}
                label={
                  <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
                    {T.OFF}
                  </Typography>
                }
                sx={{ width: '50%', mx: 0 }}
              />
            </RadioGroup>
          </FormControl>
          <Divider sx={{ my: 1 }} />
          <InheritBillingProfileCheckbox
            checked={rentalFeeOnSuspensionInherit}
            onChange={handleRentalFeeOnSuspensionInheritChange}
            billingProfileName={billingProfileName}
          >
            {billingProfile && (
              <Typography variant="body2" color="text.secondary">
                {T.SERVICE_SUSPENSION}
                {': '}
                <strong>{billingProfile.rentalFeeOnSuspensionEnabled ? T.ON : T.OFF}</strong>
              </Typography>
            )}
          </InheritBillingProfileCheckbox>
        </Paper>
        <SettingsHistoryAccordion
          versions={versions}
          versionsLoading={versionsLoading}
          hasVersions={hasVersions}
          getVersionChangeLines={getVersionChangeLines}
        />
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton disabled={!isDirty} loading={isUpdating || isFetching} onClick={handleUpdate} label={T.SAVE} />
      </HHDialogActions>
    </HHBaseDialog>
  )
}

RentalFeeSettingsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  accountId: PropTypes.string,
}

export default RentalFeeSettingsDialog
